import { BaseChildState } from '@stateMachine/BaseChildState'
import GameSignals from '../../signals/GameSignals'
import { SymbolData, WinCombination } from '@/modules'
import { SpecialSymbolType } from '@/containers/symbols/SpecialSymbolType'
import { toMajorUnits } from '@/utils/CurrencyUnits'

export class MultiplierState extends BaseChildState {
    private static readonly DEBUG_PREFIX = '[DBG:MultiplierState]'
    private _fallbackTimeoutId: number = null
    private _fsTopTotalFallbackTimeoutId: number = null
    private _isWaitingFsTopTotalUpdate: boolean = false

    public canTrigger(): boolean {
        const allWinningCombinations = this._model.spinData?.winning_combinations

        if (!Array.isArray(allWinningCombinations) || allWinningCombinations.length === 0) {
            return false
        }

        const hasRealWin = allWinningCombinations.some(
            (combinations: WinCombination[]) =>
                Array.isArray(combinations) &&
                combinations.some((combination: WinCombination) => Number(combination?.win_currency) > 0)
        )

        if (!hasRealWin) {
            return false
        }

        const bonusMultipliers = this._model.spinData?.bonus?.multipliers
        if (Array.isArray(bonusMultipliers) && bonusMultipliers.length > 0) {
            return true
        }

        const currentGrid = this._model.spinData?.slot_grid?.[this._model.cascadeIndex]
        if (!Array.isArray(currentGrid)) {
            return false
        }

        return currentGrid.some(
            (reel: SymbolData[]) =>
                Array.isArray(reel) && reel.some((symbol: SymbolData) => symbol?.id === SpecialSymbolType.MULTIPLIER)
        )
    }

    public begin() {
        GameSignals.completeMultiplierState.connect(this.onCompleteMultiplierState)
        GameSignals.multiplierState.emit(this._model.spinData.slot_grid[this._model.cascadeIndex])

        this._fallbackTimeoutId = window.setTimeout(this.onCompleteMultiplierState, 5000)
    }

    private onCompleteMultiplierState = () => {
        if (this._fallbackTimeoutId !== null) {
            clearTimeout(this._fallbackTimeoutId)
            this._fallbackTimeoutId = null
        }

        const combinationsWin: number = this._getCombinationsWinAmount()
        const totalWinCurrency = Number(this._model.spinData.win_currency || 0)
        const bonusWinCurrency = Number(this._model.spinData?.bonus?.bonus_win_currency || 0)

        let winAmount: number = totalWinCurrency - combinationsWin - bonusWinCurrency
        if (this._model.winCounter.totalWin + winAmount > 10000 * this._model.currentBet) {
            winAmount = 10000 * this._model.currentBet - this._model.winCounter.totalWin
        }

        console.log(`${MultiplierState.DEBUG_PREFIX} onCompleteMultiplierState`, {
            totalWinCurrency,
            combinationsWin,
            bonusWinCurrency,
            emittedWinAmountToPanel: winAmount,
            winCounterBefore: this._model.winCounter.totalWin,
            currentBet: this._model.currentBet,
        })

        GameSignals.updateWinPanelValue.emit(toMajorUnits(winAmount))

        const shouldWaitFsTopTotalUpdate = !!this._model?.roundState?.isFreeSpins && winAmount > 0

        const finalizeState = () => {
            if (!shouldWaitFsTopTotalUpdate) {
                this.end()
                return
            }

            this.waitForFsTopTotalUpdatedThenEnd()
        }

        if (this._model.bigWinLevel === null) {
            //When counter landed into win panel
            const winValue: number = winAmount
            console.log(`${MultiplierState.DEBUG_PREFIX} countWin(final)`, {
                winValue,
                totalWinCurrency,
                bonusWinCurrency,
            })

            if (winValue <= 0) {
                finalizeState()
                return
            }

            this._model.winCounter.countWin(winValue, -1, false, () => {
                finalizeState()
            })
            return
        }

        finalizeState()
    }

    private waitForFsTopTotalUpdatedThenEnd(): void {
        if (this._isWaitingFsTopTotalUpdate) {
            return
        }

        this._isWaitingFsTopTotalUpdate = true
        GameSignals.fsTopTotalUpdated.connect(this.onFsTopTotalUpdated)

        this._fsTopTotalFallbackTimeoutId = window.setTimeout(() => {
            this.onFsTopTotalUpdated()
        }, 3500)
    }

    private onFsTopTotalUpdated = (): void => {
        if (!this._isWaitingFsTopTotalUpdate) {
            return
        }

        this._isWaitingFsTopTotalUpdate = false

        if (this._fsTopTotalFallbackTimeoutId !== null) {
            clearTimeout(this._fsTopTotalFallbackTimeoutId)
            this._fsTopTotalFallbackTimeoutId = null
        }

        GameSignals.fsTopTotalUpdated.disconnect(this.onFsTopTotalUpdated)
        this.end()
    }

    private _getCombinationsWinAmount = (): number => {
        let winAmount: number = 0

        this._model.spinData.winning_combinations.forEach((combinations: WinCombination[]) => {
            combinations.forEach((combination: WinCombination) => {
                winAmount += combination.win_currency
            })
        })

        return winAmount
    }

    public cleanUp() {
        if (this._fallbackTimeoutId !== null) {
            clearTimeout(this._fallbackTimeoutId)
            this._fallbackTimeoutId = null
        }

        if (this._fsTopTotalFallbackTimeoutId !== null) {
            clearTimeout(this._fsTopTotalFallbackTimeoutId)
            this._fsTopTotalFallbackTimeoutId = null
        }

        this._isWaitingFsTopTotalUpdate = false

        GameSignals.completeMultiplierState.disconnect(this.onCompleteMultiplierState)
        GameSignals.fsTopTotalUpdated.disconnect(this.onFsTopTotalUpdated)
    }
}
